// Legacy: pro-frontend/src/app/manage-portfolio/, portfolio/, portfolio.service.ts

export type PortfolioTemplateKind = 'architect' | 'modern' | 'classic' | 'minimal';

export type PortfolioListItem = {
  id: number;
  title?: string | null;
  updated_at?: string | null;
  slug?: string;
  is_resume_url_public?: boolean;
  is_portfolio_url_public?: boolean;
  template?: {
    image?: string;
    preview_image_url?: string;
    previewImageUrl?: string;
    template_type?: string;
    config?: string;
  } | null;
  preview_image_url?: string | null;
  previewImageUrl?: string | null;
};

export type PortfolioListResponse = {
  status: string;
  portfolios?: PortfolioListItem[];
  message?: string;
};

export type PortfolioTemplateSelection = {
  template_id?: number | string;
  selected_theme?: 'light' | 'dark' | string;
  selected_color?: string;
  font_size?: string;
};

export type PortfolioCategoryEntry = {
  id?: number | string;
  title?: string;
  display_title?: string;
  description?: string;
  display_description?: string;
  is_hidden?: boolean;
  is_highlight?: boolean;
  work_company?: string;
  start_date_month?: number | null;
  start_date_year?: number | null;
  end_date_month?: number | null;
  end_date_year?: number | null;
  is_current?: boolean;
  ResumeEntryFile?: Array<{
    id?: number | string;
    file_url?: string;
    file_type?: string;
    file_name?: string;
    file_title?: string;
  }>;
  ResumeEntryLink?: Array<{
    id?: number | string;
    link?: string;
    title?: string;
    link_meta_title?: string;
    link_meta_description?: string;
    link_meta_image_url?: string;
  }>;
  [key: string]: unknown;
};

export type PortfolioCategory = {
  id?: number | string;
  title?: string;
  category_type?: string;
  entries?: PortfolioCategoryEntry[];
  language_display_mode?: string;
  [key: string]: unknown;
};

export type PortfolioResume = {
  id: number;
  name?: string;
  headline?: string;
  designation?: string;
  preferred_job?: string;
  preferredJob?: string;
  company?: string;
  summary?: string | { description?: string; title?: string };
  image_url?: string;
  slug?: string;
  template_id?: number;
  is_headline_hidden?: boolean;
  template?: {
    template_type?: string;
    image?: string;
    config?: string | Record<string, unknown>;
  };
  templateSelection?: PortfolioTemplateSelection;
  categories?: PortfolioCategory[];
  highlights?: PortfolioCategoryEntry[];
  twitter_url?: string;
  is_twitter_url_hidden?: boolean;
  instagram_url?: string;
  is_instagram_url_hidden?: boolean;
  facebook_url?: string;
  is_facebook_url_hidden?: boolean;
  linkedin_url?: string;
  is_linkedin_url_hidden?: boolean;
  personal_website_url?: string;
  is_personal_website_url_hidden?: boolean;
  youtube_url?: string;
  is_youtube_url_hidden?: boolean;
  vimeo_url?: string;
  is_vimeo_url_hidden?: boolean;
  dribble_url?: string;
  is_dribble_url_hidden?: boolean;
  github_url?: string;
  is_github_url_hidden?: boolean;
  carbonmade_url?: string;
  is_carbonmade_url_hidden?: boolean;
  pinterest_url?: string;
  is_pinterest_url_hidden?: boolean;
  soundcloud_url?: string;
  is_soundcloud_url_hidden?: boolean;
  tumblr_url?: string;
  is_tumblr_url_hidden?: boolean;
  portfolio_url?: string;
  is_portfolio_url_hidden?: boolean;
  is_resume_url_public?: boolean;
  is_portfolio_url_public?: boolean;
  [key: string]: unknown;
};

export type PortfolioTemplateResponse = {
  status: string;
  resume?: PortfolioResume | PortfolioResume[];
  message?: string;
};

export type PortfolioTemplatesListResponse = {
  status: string;
  templates?: Array<Record<string, unknown>>;
  portfolio_templates?: Array<Record<string, unknown>>;
  data?: {
    templates?: Array<Record<string, unknown>>;
    colors?: PortfolioColorOption[];
    themes?: Array<Record<string, unknown>>;
    fontSizes?: Array<Record<string, unknown>>;
  };
  colors?: PortfolioColorOption[];
  colorOptions?: PortfolioColorOption[];
  themes?: Array<Record<string, unknown>>;
  fontSizes?: Array<Record<string, unknown>>;
  message?: string;
};

export type PortfolioColorOption = {
  name?: string;
  primaryColor?: string;
  isDefault?: boolean;
};

export type SavePortfolioTemplateResponse = {
  status: string;
  message?: string;
  templateSelection?: PortfolioTemplateSelection;
  [key: string]: unknown;
};

export type PublicPortfolioExtraInfo = {
  deviceInfo?: Record<string, unknown>;
  isMobile?: boolean;
  screenWidth?: number;
  screenHeight?: number;
  viewType?: 'portfolio' | 'resume';
  fullUrl?: string;
};

export type PublicPortfolioResponse = {
  status: string;
  resume?: PortfolioResume | PortfolioResume[];
  visitor_id?: number;
  message?: string;
  /** Server-rendered resume HTML from `/pro/resume/public/v2` (viewType=resume). */
  html?: string | null;
};

export type PortfolioRenderData = {
  name: string;
  designation: string;
  shortBio: string;
  company: string;
  role_highlight: string;
  summary: string;
  profile_image: string;
  is_headline_hidden?: boolean;
  stats: Array<{ count: string; label: string }>;
  social: {
    twitter: string;
    linkedin: string;
    instagram: string;
    website: string;
  };
  twitter_url?: string;
  is_twitter_url_hidden?: boolean;
  instagram_url?: string;
  is_instagram_url_hidden?: boolean;
  facebook_url?: string;
  is_facebook_url_hidden?: boolean;
  linkedin_url?: string;
  is_linkedin_url_hidden?: boolean;
  personal_website_url?: string;
  is_personal_website_url_hidden?: boolean;
  youtube_url?: string;
  is_youtube_url_hidden?: boolean;
  vimeo_url?: string;
  is_vimeo_url_hidden?: boolean;
  dribble_url?: string;
  is_dribble_url_hidden?: boolean;
  github_url?: string;
  is_github_url_hidden?: boolean;
  carbonmade_url?: string;
  is_carbonmade_url_hidden?: boolean;
  pinterest_url?: string;
  is_pinterest_url_hidden?: boolean;
  soundcloud_url?: string;
  is_soundcloud_url_hidden?: boolean;
  tumblr_url?: string;
  is_tumblr_url_hidden?: boolean;
  portfolio_url?: string;
  is_portfolio_url_hidden?: boolean;
  skills: string[];
  tools: Array<{ category: string; items: string }>;
  experience: Array<{
    company: string;
    role: string;
    period: string;
    description: string;
  }>;
  achievements: Array<{ title: string; description: string; image: string }>;
  summaryTitle: string;
  summaryStatus: number;
  highlightsStatus: number;
  attachmentStatus: number;
  descriptionStatus: number;
  categories: PortfolioCategory[];
  hightlights: PortfolioCategoryEntry[];
};

export const PORTFOLIO_TEMPLATE_DATA_KEY = 'portfolioTemplateData';
export const FALLBACK_PORTFOLIO_THUMBNAIL = '/images/portfolio-templates/template-1.png';
